import React from "react";
import { useTranslation } from "react-i18next";
import LegalLayout from "../components/LegalLayout";
import { CONTACT_ADDRESS, CONTACT_EMAIL } from "../config/contact";

/**
 * GDPR Privacy Policy (Datenschutzerklärung).
 * Content is fully localized via i18n (IT/EN/DE). Controller data comes from
 * the centralized contact config so a single edit propagates everywhere.
 *
 * NOTE: the top of the page shows a visible legal-review notice — the client
 * MUST have the copy vetted by a lawyer before public launch.
 */
export default function PrivacyPage() {
  const { t } = useTranslation();

  const controller = {
    company: CONTACT_ADDRESS.company,
    address: `${CONTACT_ADDRESS.street}, ${CONTACT_ADDRESS.city}, ${CONTACT_ADDRESS.country}`,
    email: CONTACT_EMAIL,
  };

  return (
    <LegalLayout
      testid="privacy-page"
      overline={t("privacy.overline")}
      title={t("privacy.title")}
    >
      {/* Draft notice */}
      <div
        data-testid="privacy-draft-notice"
        className="mb-8 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 font-medium leading-snug"
      >
        {t("privacy.draft_notice")}
      </div>

      <div className="space-y-8 text-slate-700 leading-relaxed" data-testid="privacy-body">
        {/* Controller */}
        <Section title={t("privacy.controller_title")}>
          <p>
            {controller.company}
            <br />
            {controller.address}
          </p>
          <p className="mt-2">
            {t("privacy.controller_email_prefix")}{" "}
            <a
              href={`mailto:${controller.email}`}
              className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
              data-testid="privacy-controller-email"
            >
              {controller.email}
            </a>
          </p>
        </Section>

        {/* Data collected */}
        <Section title={t("privacy.data_title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("privacy.data_account")}</li>
            <li>{t("privacy.data_requests")}</li>
            <li>{t("privacy.data_logs")}</li>
          </ul>
        </Section>

        {/* Legal basis */}
        <Section title={t("privacy.legal_basis_title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("privacy.legal_contract")}</li>
            <li>{t("privacy.legal_interest")}</li>
            <li>{t("privacy.legal_consent")}</li>
          </ul>
        </Section>

        {/* Retention */}
        <Section title={t("privacy.retention_title")}>
          <p>{t("privacy.retention_body")}</p>
        </Section>

        {/* Rights */}
        <Section title={t("privacy.rights_title")}>
          <p>{t("privacy.rights_intro")}</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>{t("privacy.rights_access")}</li>
            <li>{t("privacy.rights_rectify")}</li>
            <li>{t("privacy.rights_erase")}</li>
            <li>{t("privacy.rights_restrict")}</li>
            <li>{t("privacy.rights_portability")}</li>
            <li>{t("privacy.rights_object")}</li>
          </ul>
          <p className="mt-3">{t("privacy.rights_supervisory")}</p>
        </Section>

        {/* Cookies */}
        <Section title={t("privacy.cookies_title")}>
          <p>{t("privacy.cookies_body")}</p>
        </Section>

        {/* Hosting placeholder */}
        <Section title={t("privacy.hosting_title")}>
          <p className="italic text-slate-500">[Hosting provider – da definire prima del lancio]</p>
        </Section>

        {/* Analytics placeholder */}
        <Section title={t("privacy.analytics_title")}>
          <p className="italic text-slate-500">
            [Strumenti di analytics/cookie – da definire prima del lancio]
          </p>
        </Section>

        {/* Payment partner */}
        <Section title={t("privacy.payment_title")}>
          <p>{t("privacy.payment_body")}</p>
          <p className="mt-2 italic text-slate-500">
            [Nome partner di pagamento – da definire]
          </p>
        </Section>
      </div>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-semibold text-lg text-slate-950 mb-2">{title}</h2>
      {children}
    </section>
  );
}
